// react
import { SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';

// router
import { Link as RouterLink, Outlet, useSearchParams, useLocation } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import {
	ContextAccountsDesignated,
	ContextAccountsLocal,
	ContextProject,
} from '../Project.context';

// mui
import { Box, Container, Grid, Typography } from '@mui/material';

// icons
import { AccountBalanceWallet as IconAccount } from '@mui/icons-material';

// dialogs
import { DialogAddAccount } from '../../../dialogs/DialogAddAccount/DialogAddAccount';

// v2 listing header
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// ui
import { Tabs } from '../../../ui/Tabs/Tabs';

export const Accounts = () => {
	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const {
		isOnboarded: isProjectOnboarded,
		project,
	} = useContext(ContextProject);

	// hooks
	const location = useLocation();
	const { t } = useTranslation();

	// context
	const { refetchAccountsDesignated } = useContext(ContextAccountsDesignated);
	const { refetchAccountsLocal } = useContext(ContextAccountsLocal);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogType = searchParams.get('dialogType');

	// vars
	const tabs = useMemo(() => {
		const tabs = [
			{
				component: RouterLink,
				label: t('designated'),
				to: `/project/${project?.idWbg}/accounts/designated`,
				value: 'designated',
			},
		];

		if (isProjectOnboarded) {
			tabs.push({
				component: RouterLink,
				label: t('local'),
				to: `/project/${project?.idWbg}/accounts/local`,
				value: 'local',
			});
		}

		return tabs;
	}, [isProjectOnboarded, project, t]);

	// defaults
	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('local')) {
				return 'local';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [tabValue, setTabValue] = useState(defaultTabValue);

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	return (
		<>
			{(project?.version === '2') && (
				<HeaderListingPage
					icon={IconAccount}
					title={t('accounts')}
					subtitle={t('accounts.subtitle')}
					variant="financial"
				/>
			)}
			<Box sx={{ pt: (project?.version === '2') ? 3 : 0 }}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Typography variant="h2">{t('accounts')}</Typography>
					</Container>
				</Grid>
				<Grid item xs={12}>
					<Tabs hasBorder={false} onChange={handleChangeTab} tabs={tabs} value={tabValue} />
				</Grid>
				<Grid item xs={12}>
					<Outlet />
				</Grid>
			</Grid>
			</Box>
			<DialogAddAccount
				isOpen={paramDialog === 'addAccount'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						params.delete('dialogAccountName');
						params.delete('dialogInstitution');
						params.delete('dialogRouteCode');
						params.delete('dialogSwiftCode');
						params.delete('dialogType');
						return params;
					});
				}}
				onSuccess={() => {
					if (paramDialogType) {
						if (paramDialogType === 'DESIGNATED') {
							refetchAccountsDesignated();
						} else if (paramDialogType === 'LOCAL') {
							refetchAccountsLocal();
						}
					} else {
						refetchAccountsDesignated();
						refetchAccountsLocal();
					}
				}}
			/>
		</>
	);
};
